<script setup lang="ts">
import { computed, onBeforeMount, watch } from "vue";
import { useRoute, onBeforeRouteLeave } from "vue-router";
import { useApiClient } from "@/api/client";
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import { useRuleModelVersion } from "@/modules/rule_engine/composables/useRuleModelVersion";
import { useRuleHistoryShortcuts } from "@/modules/rule_engine/composables/useRuleHistoryShortcuts";
import { useRuleEngineAiSummaryChannel } from "@/modules/rule_engine/composables/useRuleEngineAiSummaryChannel";
import {
	parseAiSummary,
	RULE_ENGINE_AI_SUMMARY_PERMISSION,
} from "@/modules/rule_engine/aiSummaryFormat";
import { useBaseStore } from "@/stores/base";
import RuleList from "@/modules/rule_engine/components/RuleList.vue";
import HistoryControls from "@/modules/rule_engine/components/ui/HistoryControls.vue";
import RuleSetVersionType = App.Enums.RuleSetVersionType;

const props = defineProps<{ scenario: RuleSetVersionType }>();

const route = useRoute();
const store = useRuleEnginedStore();

const model_uuid = String(route.params.model_uuid);
const version_uuid = String(route.params.version_uuid);

const { isInitialized, initialize, saveAsVersion } = useRuleModelVersion(
	model_uuid,
	version_uuid,
	props.scenario
);

useRuleEngineAiSummaryChannel(version_uuid);

// Each scenario tab is its own root rule set (per_transaction vs per_day),
// so its summary is scoped to that rule set, not the whole model version.
const scenarioAiSummary = computed(() =>
	store.ruleSetAiSummary(store.parsedModelType?.rule_set?.uuid)
);

const scenarioAiSummaryParsed = computed(() =>
	parseAiSummary(scenarioAiSummary.value?.ai_summary ?? "")
);

const hasAiSummaryPermission = computed(() =>
	useBaseStore().HAS_PERMISSION(RULE_ENGINE_AI_SUMMARY_PERMISSION)
);

// An existing summary describes the last-saved state, so unsaved edits to
// this scenario (or a nested group within it) hide it rather than show it
// as still accurate.
const scenarioHasUnsavedChanges = computed(() =>
	store.ruleSetHasUnsavedChanges(store.parsedModelType?.rule_set?.uuid)
);

/**
 * Todos and labels back the "add task"/"add label" action pickers, so they have
 * to be in the store before any rule is opened for editing.
 */
const loadActionValues = async () => {
	const paginationParams = { page: 1, per_page: 100000, sortBy: [] };

	const [todoResponse, labelResponse] = await Promise.all([
		useApiClient("proLoyalty").getTodos(paginationParams),
		useApiClient("proLoyalty").getLabels(paginationParams),
	]);

	if (todoResponse.isOk()) {
		store.ruleEngineState.values_for_action_type.add_task_to_bex_reservation =
			todoResponse.value.data;
	}

	if (labelResponse.isOk()) {
		store.ruleEngineState.values_for_action_type.add_label_to_bex_reservation =
			labelResponse.value.data;
	}
};

onBeforeMount(() => initialize(loadActionValues));

useRuleHistoryShortcuts();

// The parent owns the scenario tabs; follow whichever one is active.
watch(
	() => props.scenario,
	(scenario) => {
		store.ruleEngineState.selectedModelVersionType = scenario;
		store.ruleEngineState.displayedGroupUuid =
			store.parsedModelType?.uuid ?? null;
	}
);

onBeforeRouteLeave((to, from, next) => {
	const leavingBuilder = !to.name
		?.toString()
		.startsWith("pro_loyalty.scenarios.builder");

	unsavedChangesDialog(
		"scenario",
		leavingBuilder && store.parsedModelHasChanges,
		next
	);
});
</script>

<template>
	<div v-if="isInitialized && store.parsedModelType">
		<!-- scenario AI summary — always the same collapsible container,
		     regardless of state, so switching between dirty/generating/
		     failed/completed never changes the collapsed header's height
		     and jumps the page. -->
		<div v-if="hasAiSummaryPermission" class="mb-4">
			<v-expansion-panels class="ai-summary">
				<v-expansion-panel elevation="0" class="rounded-lg bg-grey-lighten-3">
					<v-expansion-panel-title>AI summary</v-expansion-panel-title>
					<v-expansion-panel-text>
						<div
							v-if="scenarioHasUnsavedChanges"
							class="text-medium-emphasis"
						>
							AI summary will be available after saving this scenario.
						</div>
						<div
							v-else-if="scenarioAiSummary?.ai_summary_status === 'failed'"
							class="text-medium-emphasis"
						>
							Summary unavailable.
						</div>
						<div
							v-else-if="scenarioAiSummary?.ai_summary_status !== 'completed'"
							class="d-flex align-center text-medium-emphasis"
						>
							<v-progress-circular indeterminate size="16" width="2" class="mr-2" />
							Generating summary...
						</div>
						<div v-else class="ai-summary__text">
							<p v-if="scenarioAiSummaryParsed.lead" class="ai-summary__lead">
								{{ scenarioAiSummaryParsed.lead }}
							</p>
							<ul
								v-if="scenarioAiSummaryParsed.bullets.length"
								class="ai-summary__list"
							>
								<li
									v-for="(bullet, i) in scenarioAiSummaryParsed.bullets"
									:key="i"
								>
									{{ bullet }}
								</li>
							</ul>
						</div>
					</v-expansion-panel-text>
				</v-expansion-panel>
			</v-expansion-panels>
		</div>

		<div class="d-flex pb-2">
			<v-spacer />
			<history-controls />
		</div>

		<rule-list
			v-model="store.parsedModelType.rule_set.rules"
			:allowed-rule-types="['condition']"
		/>

		<div class="d-flex pt-5">
			<v-spacer />
			<v-btn
				color="primary"
				variant="flat"
				:disabled="!store.parsedModelHasChanges"
				@click="saveAsVersion('replace')"
			>
				Save
			</v-btn>
		</div>
	</div>
	<v-progress-linear v-else indeterminate color="primary" height="5" />
</template>

<style scoped lang="scss">
.ai-summary__text {
	overflow-wrap: anywhere;
	line-height: 1.5;
	font-size: 0.9rem;
}

.ai-summary__lead {
	margin: 0 0 0.6rem;
}

.ai-summary__list {
	margin: 0;
	padding-left: 1.2rem;

	li {
		margin-bottom: 0.4rem;

		&::marker {
			color: rgb(var(--v-theme-primary));
		}

		&:last-child {
			margin-bottom: 0;
		}
	}
}
</style>
